/**
 * Batch-6 verlof — pure berekeningen voor ouderschapsverlof (Wet betaald
 * ouderschapsverlof). Recht is PER KIND; alle parameters komen uit het
 * `VerlofBeleid` (geen wettelijke/CAO-waarde hardcoded in deze functies).
 *
 * Formule (spec Deel 2b):
 *   totaal    = ouderschapTotaalWeken × contracturenPerWeek
 *   betaald   = ouderschapBetaaldWeken × contracturenPerWeek
 *   onbetaald = totaal − betaald
 *   minus reedsOpgenomenVorigeWerkgeverUren (eerst van betaald wanneer
 *   betaaldDeelGebruikt, anders van onbetaald; een overschot loopt door naar
 *   het andere deel zodat het totaal nooit dubbel telt)
 *   vervaldata: betaald = geboortedatum + betaaldBinnenMaandenNaGeboorte;
 *               totaal  = de dag vóór de (instelbare, default 8e) verjaardag
 *
 * Voorbeelden (testcases): 38u/week → 988 totaal / 342 betaald / 646
 * onbetaald; 28u/week → 728 / 252 / 476.
 *
 * Datums zijn UTC-middernacht van de NL-kalenderdag (repo-conventie; zie
 * contract-uren.ts) — alle datum-rekenkunde hier gaat via de getUTC/setUTC-
 * varianten.
 */

import {
  OUDERSCHAP_VERLOOPT_BINNENKORT_DAGEN,
  OUDERSCHAP_VERVAL_ROLLBACK_PREFIX,
  type OuderschapEmmerStatus,
} from '@streep/types'
import { vandaagAmsterdam } from './dagtaken-datum'
import {
  berekenSaldoOpDatum,
  round2,
  type SaldoEmmerType,
  type SaldoMutatieInfo,
} from './leave-calculations'

export interface OuderschapsBeleidInput {
  /** Totaal recht in weken (wettelijk NL 2026: 26). */
  totaalWeken: number
  /** Betaald (UWV/WAZO-)deel in weken (wettelijk NL 2026: 9). */
  betaaldWeken: number
  /** Recht geldt tot deze verjaardag van het kind (wettelijk: 8). */
  kindTotLeeftijd: number
  /** Betaald deel opnemen binnen X maanden na geboorte (wettelijk: 12). */
  betaaldBinnenMaandenNaGeboorte: number
}

export interface OuderschapsKindInput {
  geboortedatum: Date
  /**
   * True wanneer het betaalde deel (deels) bij een vorige werkgever is
   * gebruikt — de verrekening gaat dan eerst van het betaalde deel af.
   */
  betaaldDeelGebruikt: boolean
  /** Bij een vorige werkgever reeds opgenomen uren (verrekening). */
  reedsOpgenomenVorigeWerkgeverUren: number
}

export interface OuderschapsRecht {
  totaalUren: number
  betaaldUren: number
  onbetaaldUren: number
  /** Uiterste opnamedatum van het betaalde deel (einde van die dag). */
  betaaldExpiresAt: Date
  /** Verval van het totale recht: de dag vóór de verjaardag. */
  totaalExpiresAt: Date
}

/** UTC-veilige "plus N maanden" (clamp op maandeinde: 31 jan + 1m → 28/29 feb). */
export function addMaandenUtc(datum: Date, maanden: number): Date {
  const jaar = datum.getUTCFullYear()
  const maand = datum.getUTCMonth() + maanden
  const dag = datum.getUTCDate()
  const result = new Date(Date.UTC(jaar, maand, 1))
  const dagenInDoelMaand = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
  ).getUTCDate()
  result.setUTCDate(Math.min(dag, dagenInDoelMaand))
  return result
}

/** De dag vóór de `leeftijd`-de verjaardag (UTC), bv. verval totaalrecht. */
export function dagVoorVerjaardag(geboortedatum: Date, leeftijd: number): Date {
  const verjaardag = new Date(
    Date.UTC(
      geboortedatum.getUTCFullYear() + leeftijd,
      geboortedatum.getUTCMonth(),
      geboortedatum.getUTCDate(),
    ),
  )
  verjaardag.setUTCDate(verjaardag.getUTCDate() - 1)
  return verjaardag
}

/**
 * Bereken het ouderschapsverlof-recht voor één kind op basis van de
 * contracturen per week op de berekendatum. Pro-rata voor parttimers zit in
 * de weken×uren-formule zelf (28u-contract → 728u i.p.v. 988u).
 */
export function berekenOuderschapsRecht(
  kind: OuderschapsKindInput,
  beleid: OuderschapsBeleidInput,
  contractUrenPerWeek: number,
): OuderschapsRecht {
  const brutoBetaald = round2(beleid.betaaldWeken * contractUrenPerWeek)
  const brutoOnbetaald = round2(
    (beleid.totaalWeken - beleid.betaaldWeken) * contractUrenPerWeek,
  )

  // Verrekening vorige werkgever: eerst van het deel dat daar gebruikt is;
  // een overschot loopt door naar het andere deel (totaal klopt altijd).
  const verrekening = Math.max(0, kind.reedsOpgenomenVorigeWerkgeverUren)
  let betaald = brutoBetaald
  let onbetaald = brutoOnbetaald
  if (kind.betaaldDeelGebruikt) {
    const vanBetaald = Math.min(betaald, verrekening)
    betaald = round2(betaald - vanBetaald)
    onbetaald = round2(Math.max(0, onbetaald - (verrekening - vanBetaald)))
  } else {
    const vanOnbetaald = Math.min(onbetaald, verrekening)
    onbetaald = round2(onbetaald - vanOnbetaald)
    betaald = round2(Math.max(0, betaald - (verrekening - vanOnbetaald)))
  }

  return {
    totaalUren: round2(betaald + onbetaald),
    betaaldUren: betaald,
    onbetaaldUren: onbetaald,
    betaaldExpiresAt: addMaandenUtc(kind.geboortedatum, beleid.betaaldBinnenMaandenNaGeboorte),
    totaalExpiresAt: dagVoorVerjaardag(kind.geboortedatum, beleid.kindTotLeeftijd),
  }
}

/**
 * Arbeidsduurwijziging (spec Deel 2b): het RESTERENDE recht wordt herrekend
 * naar de nieuwe weekuren; reeds opgenomen uren blijven historisch staan.
 *
 * Keuze (gedocumenteerd): we rekenen het resterende deel terug naar weken via
 * de contracturen waarmee het recht laatst berekend is
 * (`berekendMetUrenPerWeek`, gesnapshot op de entitlement) en vermenigvuldigen
 * die rest-weken met de nieuwe weekuren:
 *
 *   restWeken  = (hours − usedHours) / berekendMetUrenPerWeek
 *   nieuwHours = usedHours + restWeken × nieuweUrenPerWeek
 *
 * Idempotent: nogmaals draaien met dezelfde nieuwe uren wijzigt niets, omdat
 * de snapshot na herberekening op de nieuwe uren staat.
 */
export function herberekenOuderschapsRestRecht(input: {
  hours: number
  usedHours: number
  berekendMetUrenPerWeek: number
  nieuweUrenPerWeek: number
}): number {
  if (input.berekendMetUrenPerWeek <= 0) return round2(input.hours)
  const restWeken = Math.max(0, (input.hours - input.usedHours) / input.berekendMetUrenPerWeek)
  return round2(input.usedHours + restWeken * input.nieuweUrenPerWeek)
}

/** Is het betaalde deel nog opneembaar op `datum` (t/m de vervaldag zelf)? */
export function betaaldDeelNogOpneembaar(betaaldExpiresAt: Date, datum: Date): boolean {
  const grens = new Date(
    Date.UTC(
      betaaldExpiresAt.getUTCFullYear(),
      betaaldExpiresAt.getUTCMonth(),
      betaaldExpiresAt.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  )
  return datum.getTime() <= grens.getTime()
}

// ── batch-71 ouderschap-verlof (FO-539 §A): één saldo-berekening ─────────────

/** De emmer-velden die de stand nodig heeft (uit `LeaveEntitlement`, als getallen). */
export interface OuderschapEmmerInput {
  id: string
  type: Extract<SaldoEmmerType, 'OUDERSCHAP_BETAALD' | 'OUDERSCHAP_ONBETAALD' | 'SENIORENVERLOF'>
  hours: number
  usedHours: number
  expiresAt: Date
  expired: boolean
}

/** Een `SaldoMutatie` van die emmer; `bron` scheidt een terugboeking van een echte correctie. */
export interface OuderschapMutatieInput {
  richting: SaldoMutatieInfo['richting']
  bron: string
  uren: number
  datum: Date
  /**
   * Herkent de tegenboeking van een teruggedraaide correctierun
   * (`OUDERSCHAP_VERVAL_ROLLBACK_PREFIX`): die maakt een VERVAL ongedaan en is geen opbouw.
   */
  bronReferentie?: string | null
}

/** De tegenboeking van een VERVAL uit een teruggedraaide correctierun (spec 239 §8). */
function isVervalTerugdraai(m: OuderschapMutatieInput): boolean {
  return m.richting === 'CORRECTIE' && (m.bronReferentie ?? '').startsWith(OUDERSCHAP_VERVAL_ROLLBACK_PREFIX)
}

export interface OuderschapEmmerStand {
  toegekend: number
  opgenomen: number
  /** Vervallen uren: de VERVAL-mutaties plus, na de termijn, het restant dat nog niet geboekt is. */
  vervallen: number
  /** Nog op te nemen uren; 0 bij een verlopen emmer en nooit negatief. */
  beschikbaar: number
  verlopen: boolean
  vervaltOp: Date
  status: OuderschapEmmerStatus
  /**
   * Netto opname met een boekdatum na de vervaldag (spec 239 §7.5). Blijft staan en telt
   * niet als beschikbaar. Let op: de classificatie boekt op de dag van classificeren, dus
   * een opname binnen de termijn die laat is verwerkt, staat hier ook.
   */
  opgenomenNaTermijn: number
  /** Meer opgenomen dan het recht (toegekend plus correcties); `beschikbaar` blijft 0. */
  bovenRecht: number
}

/** De bron van een terugboeking of herboeking: geen correctie maar het verbruik zelf. */
const OPNAME_BRON = 'VERLOFAANVRAAG'

/**
 * Eén saldo-berekening voor alle ouderschapsweergaven (spec 239 §A1), bovenop
 * `berekenSaldoOpDatum` (toegekend, opbouw, opgenomen, VERVAL-mutaties, read-time
 * verval), met drie aanvullingen die voor een kind-emmer nodig zijn:
 *
 *  - Een opname telt meteen mee, ook als de dag nog moet komen: de boekpagina boekt
 *    een weekpatroon op de opnamedag zelf, en die uren zijn al van het recht af. De
 *    peildatum gaat daarom niet als datumfilter de ledger in.
 *  - `opgenomen` is het netto verbruik. De ledger heeft een terugboeking als losse
 *    CORRECTIE staan (bron VERLOFAANVRAAG); die verrekent hier met de afname, zodat
 *    een ingetrokken dag niet als opgenomen blijft staan. Een handmatige correctie
 *    door HR telt wel gewoon mee (de oude formule `hours - usedHours` miste die).
 *  - Ledger en cache (`usedHours`) horen gelijk te zijn. Wijken ze af, dan telt het
 *    hoogste verbruik: liever te weinig beloven dan uren tonen die al op zijn.
 *
 * Verlopen is `expired` of de NL-kalenderdag voorbij de vervaldag (t/m de vervaldag
 * zelf mag opnemen, zie `betaaldDeelNogOpneembaar`). Daarmee klopt de weergave ook
 * als de cron nog niet heeft gedraaid; de cron blijft de bron van de VERVAL-mutatie.
 */
export function berekenOuderschapEmmerStand(
  emmer: OuderschapEmmerInput,
  mutaties: ReadonlyArray<OuderschapMutatieInput>,
  peildatum: Date,
  opties: { verlooptBinnenkortDagen?: number } = {},
): OuderschapEmmerStand {
  const peildag = vandaagAmsterdam(peildatum)
  const verlopen = emmer.expired || !betaaldDeelNogOpneembaar(emmer.expiresAt, peildag)

  const nettoAfname = round2(
    mutaties.reduce((som, m) => {
      if (m.richting === 'AFNAME' || m.richting === 'UITBETALING') return som + Math.abs(m.uren)
      if (m.richting === 'CORRECTIE' && m.bron === OPNAME_BRON) return som - m.uren
      return som
    }, 0),
  )
  const opgenomen = Math.max(nettoAfname, round2(emmer.usedHours))

  // De VERVAL-mutaties netto: een teruggedraaide correctierun boekt een tegenboeking
  // (CORRECTIE) en de cron laat de emmer daarna opnieuw vervallen. Telde die tegenboeking
  // als opbouw, dan stonden er twee keer zoveel uren als "vervallen" (review I5).
  const nettoVerval = Math.max(
    0,
    round2(
      mutaties.reduce((som, m) => {
        if (m.richting === 'VERVAL') return som + Math.abs(m.uren)
        if (isVervalTerugdraai(m)) return som - m.uren
        return som
      }, 0),
    ),
  )

  // De ledger voor `berekenSaldoOpDatum`: de correcties zoals ze staan, het netto verval
  // en het netto verbruik elk als één regel. De datum doet niet mee (zie boven): alles
  // staat op de peildatum en de vervaldatum alleen bij een verlopen emmer.
  const ledger: SaldoMutatieInfo[] = mutaties
    .filter(
      (m) =>
        m.richting !== 'AFNAME' &&
        m.richting !== 'UITBETALING' &&
        m.richting !== 'VERVAL' &&
        !isVervalTerugdraai(m) &&
        !(m.richting === 'CORRECTIE' && m.bron === OPNAME_BRON),
    )
    .map((m, i) => ({
      id: `${emmer.id}:${i}`,
      emmerId: emmer.id,
      richting: m.richting,
      uren: m.uren,
      datum: peildatum,
      vervalDatum: null,
    }))
  if (nettoVerval > 0) {
    ledger.push({
      id: `${emmer.id}:verval`,
      emmerId: emmer.id,
      richting: 'VERVAL',
      uren: -nettoVerval,
      datum: peildatum,
      vervalDatum: null,
    })
  }
  ledger.push({
    id: `${emmer.id}:opgenomen`,
    emmerId: emmer.id,
    richting: 'AFNAME',
    uren: -opgenomen,
    datum: peildatum,
    vervalDatum: null,
  })
  const stand = berekenSaldoOpDatum(
    {
      id: emmer.id,
      type: emmer.type,
      jaar: 0,
      toegekend: emmer.hours,
      opgenomen,
      // Read-time verval gaat in `berekenSaldoOpDatum` op `peildatum > vervalDatum`. Of de
      // emmer verlopen is staat hierboven al vast (in NL-tijd), ook bij `expired` met een
      // vervaldag in de toekomst; daarom een grens net voor de peildatum.
      vervalDatum: verlopen ? new Date(peildatum.getTime() - 1) : null,
      toekenningsDatum: emmer.expiresAt,
    },
    ledger,
    peildatum,
    0,
  )

  const opbouw = ledger
    .filter((m) => m.richting === 'OPBOUW' || m.richting === 'OVERDRACHT' || m.richting === 'CORRECTIE')
    .reduce((som, m) => som + m.uren, 0)
  // Het recht: toegekend plus wat HR erbij of eraf boekte. De tegenboeking van een
  // teruggedraaide run en het verbruik zelf zitten hier niet in.
  const recht = round2(emmer.hours + opbouw)

  // Verlopen: wat niet is opgenomen, is vervallen, los van hoe het verval in de ledger
  // staat. Een opname binnen de termijn die pas na de verval-cron is geboekt, telt zo
  // gewoon als opgenomen, en de getallen tellen altijd op tot het recht (review I5/I6):
  // opgenomen + vervallen = recht + bovenRecht.
  const vervallen = verlopen
    ? Math.max(0, round2(recht - opgenomen))
    : round2(emmer.hours + opbouw - stand.opgebouwd)
  const beschikbaar = verlopen ? 0 : Math.max(0, round2(stand.beschikbaar))
  const bovenRecht = verlopen
    ? Math.max(0, round2(opgenomen - recht))
    : Math.max(0, round2(-stand.beschikbaar))
  const naVervaldag = mutaties
    .filter((m) => m.datum.getTime() > eindVanDag(emmer.expiresAt))
    .reduce((som, m) => {
      if (m.richting === 'AFNAME') return som + Math.abs(m.uren)
      if (m.richting === 'CORRECTIE' && m.bron === OPNAME_BRON) return som - m.uren
      return som
    }, 0)

  const dagenTotVerval = Math.round((emmer.expiresAt.getTime() - peildag.getTime()) / 86_400_000)
  const grens = opties.verlooptBinnenkortDagen ?? OUDERSCHAP_VERLOOPT_BINNENKORT_DAGEN
  const status: OuderschapEmmerStatus = verlopen
    ? 'VERLOPEN'
    : beschikbaar > 0 && dagenTotVerval <= grens
      ? 'VERLOOPT_BINNENKORT'
      : 'GELDIG'

  return {
    toegekend: round2(emmer.hours),
    opgenomen,
    vervallen,
    beschikbaar,
    verlopen,
    vervaltOp: emmer.expiresAt,
    status,
    opgenomenNaTermijn: Math.max(0, round2(naVervaldag)),
    bovenRecht,
  }
}

function eindVanDag(dag: Date): number {
  return Date.UTC(dag.getUTCFullYear(), dag.getUTCMonth(), dag.getUTCDate(), 23, 59, 59, 999)
}

/**
 * Wat er met een emmer voorbij de termijn gebeurt (spec 239 §C6, §7.3). De verval-cron
 * en de correctierun pakken alleen `TE_VERVALLEN` op; de drie categorieën daarna staan
 * op de HR-lijst, want beschermd of niet-definitief betekent niet onzichtbaar.
 */
export type OuderschapVervalCategorie =
  | 'NOG_GELDIG'
  | 'VERVALLEN'
  | 'TE_VERVALLEN'
  | 'BESCHERMD'
  | 'NIET_DEFINITIEF'
  | 'INCONSISTENT'

export function classificeerOuderschapVerval(
  emmer: {
    expiresAt: Date
    expired: boolean
    status: string
    vervalBeschermd: boolean
  },
  peildatum: Date,
): OuderschapVervalCategorie {
  const termijnVoorbij = !betaaldDeelNogOpneembaar(emmer.expiresAt, vandaagAmsterdam(peildatum))
  if (emmer.expired) return termijnVoorbij ? 'VERVALLEN' : 'INCONSISTENT'
  if (!termijnVoorbij) return 'NOG_GELDIG'
  if (emmer.status !== 'DEFINITIEF') return 'NIET_DEFINITIEF'
  return emmer.vervalBeschermd ? 'BESCHERMD' : 'TE_VERVALLEN'
}

// ── batch-71 ouderschap-verlof (FO-540 §3.2): aanvullen met vakantie-uren ─────

/**
 * De aanvulling op een opname: `opnameUren x (1 - doorbetalingPct)`, per opname afgerond
 * op 2 decimalen (half naar boven). 7,6 uur bij 0,70 geeft 2,28 uur; 36 van die dagen samen
 * 82,08 uur (de praktijkcase uit de spec).
 *
 * Exact gerekend in hele honderdsten uur en tienduizendsten percentage (de precisie van de
 * kolommen `Decimal(8,2)` en `Decimal(5,4)`), zodat er geen binaire afrondingsfout in het
 * saldo sluipt. `@streep/utils` heeft bewust geen decimal-library.
 */
export function berekenAanvulUren(opnameUren: number, doorbetalingPct: number): number {
  if (!(opnameUren > 0)) return 0
  const urenHonderdsten = Math.round(opnameUren * 100)
  const pctTienduizendsten = Math.min(10_000, Math.max(0, Math.round(doorbetalingPct * 10_000)))
  const aanvullingTienduizendsten = 10_000 - pctTienduizendsten
  return Math.floor((urenHonderdsten * aanvullingTienduizendsten + 5_000) / 10_000) / 100
}
